import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import {
  DeductionRow,
  DiscountLabel,
  DueNowTotalRow,
  ThereafterRow,
} from "@/components/Layout/PriceTable/DueNowRows";
import { PriceRow } from "@/components/Layout/PriceTable/PriceRow";
import { availableCurrencies, getCurrency } from "@/utils/translationHelpers";

export const SubscriptionDueNow = ({
  icon,
  totalOnceAmount,
  discountOnceAmount,
  discountName,
  discountEndDate,
  credit,
  creditTimeRemaining,
  currency,
  dataRateName,
  dataRatePrice,
  voiceOptionPrice,
  billingPeriod,
  watchSimName,
  watchSimPrice,
  recurringAmount,
  recurringDate,
  recurringBillingPeriod,
  recurringCaption,
}: {
  icon?: JSX.Element;
  totalOnceAmount: number;
  discountOnceAmount?: number;
  discountName?: string;
  discountEndDate?: Date;
  credit?: number;
  creditTimeRemaining?: number;
  currency: availableCurrencies;
  dataRateName?: string;
  dataRatePrice?: number;
  voiceOptionPrice?: number;
  billingPeriod: string;
  watchSimName?: string;
  watchSimPrice?: number;
  recurringAmount?: number;
  recurringDate?: string;
  recurringBillingPeriod?: string;
  recurringCaption?: string;
}) => {
  const { t } = useTranslation();
  const { t: tCheckout } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.checkout",
  });

  const showVoiceOption =
    !dataRateName?.startsWith("Flat Mobile") &&
    voiceOptionPrice !== undefined &&
    voiceOptionPrice >= 0;

  const resolvedDiscountName =
    discountName ?? t("Onboarding.sections.checkout.summary.discount");

  return (
    <CardModule
      className="self-start"
      variant="white"
      title={t("portal:common.checkout.nowToPay")}
      actions={icon ? <div id="device-icon">{icon}</div> : undefined}
    >
      <div className="flex w-full flex-col">
        {!!dataRateName &&
          dataRatePrice !== undefined &&
          dataRatePrice >= 0 && (
            <PriceRow
              withBackdrop
              variant="strong"
              label={dataRateName}
              value={getCurrency(dataRatePrice, currency)}
            />
          )}

        {showVoiceOption && (
          <PriceRow
            label={
              <>
                {t("common.device.attributes.voiceOption")}
                <br />
                <span className="text-body-xs font-regular leading-md">
                  {billingPeriod}
                </span>
              </>
            }
            value={getCurrency(voiceOptionPrice, currency)}
          />
        )}

        {!!watchSimName &&
          watchSimPrice !== undefined &&
          watchSimPrice >= 0 && (
            <PriceRow
              label={
                <>
                  {watchSimName}
                  <br />
                  <span className="text-body-xs font-regular leading-md">
                    {billingPeriod}
                  </span>
                </>
              }
              value={getCurrency(watchSimPrice, currency)}
            />
          )}

        {!!discountOnceAmount && discountOnceAmount > 0 && (
          <DeductionRow
            dataTestid="subscription-checkout-discount"
            label={
              <DiscountLabel
                name={resolvedDiscountName}
                endDate={discountEndDate}
              />
            }
            amount={discountOnceAmount}
          />
        )}

        {!!creditTimeRemaining && creditTimeRemaining > 0 && (
          <DeductionRow
            dataTestid="subscription-checkout-credit-time-remaining"
            withDivider
            label={tCheckout("creditRemainingTime")}
            amount={creditTimeRemaining}
          />
        )}

        {!!credit && credit > 0 && (
          <DeductionRow
            dataTestid="subscription-checkout-credit"
            withDivider
            label={t("portal:common.checkout.creditOther")}
            amount={credit}
          />
        )}

        <DueNowTotalRow
          dataTestid="subscription-checkout-total"
          label={t("portal:common.checkout.sum")}
          amount={totalOnceAmount}
          currency={currency}
          caption={recurringCaption}
        />

        {recurringAmount !== undefined &&
          recurringAmount !== Math.max(totalOnceAmount, 0) && (
            <ThereafterRow
              dataTestid="subscription-checkout-recurring"
              amount={recurringAmount}
              currency={currency}
              date={recurringDate}
              billingPeriod={recurringBillingPeriod}
            />
          )}
      </div>
    </CardModule>
  );
};
